import { CollectionHelpers } from 'src/helpers/CollectionHelpers';
import { SWRResponse } from 'swr';
import useSWRImmutable from 'swr/immutable';

import { CompanyBase, CompanyProfile, ListResponse, ManufacturerProfile } from 'models';
import { CompanyHelpers } from 'helpers/CompanyHelpers';

import { prepareConditionalSwrArgs } from 'hooks/prepare-conditional-swr-args';
import { CompanyProfileListProps, CompanyProfileService } from 'services/CompanyProfileService';

const useCompanyProfiles = (
    props?: CompanyProfileListProps & { execute?: boolean; keepPreviousData?: boolean },
): SWRResponse & {
    companies: CompanyProfile[];
    page: number;
    totalPages: number;
    totalDocs: number;
} => {
    const { execute = true, keepPreviousData = true, ...listProps } = props || {};

    const swr = useSWRImmutable<ListResponse<ManufacturerProfile>>(
        ...prepareConditionalSwrArgs({
            key: `/api/profile?query=${JSON.stringify(listProps)}`,
            fetcher: () => CompanyProfileService.list(listProps),
            condition: execute,
        }),
        {
            keepPreviousData,
        },
    );

    return {
        ...swr,
        companies: swr?.data?.docs || [],
        page: swr?.data?.page || 1,
        totalPages: swr?.data?.totalPages || 1,
        totalDocs: swr?.data?.totalDocs || 0,
    };
};

const useCompanyProfilesByIds = (
    profiles: (CompanyBase | CompanyBase['id'])[] = [],
): SWRResponse & {
    companies: CompanyProfile[];
} => {
    const profileIds = profiles.map((profile) => CollectionHelpers.id(profile));

    const swr = useSWRImmutable(
        ...prepareConditionalSwrArgs({
            key: CompanyHelpers.swr.profiles(profileIds),
            fetcher: () => Promise.all(profileIds.map((profileId) => CompanyProfileService.get(profileId))),
            condition: profileIds.length > 0,
        }),
    );

    return {
        ...swr,
        companies: (swr.data ?? []) as CompanyProfile[],
    };
};

export { useCompanyProfiles, useCompanyProfilesByIds };
